// import libraries
import { onAuthStateChanged, onIdTokenChanged, signOut, getIdTokenResult } from 'firebase/auth';
import { auth } from '../../../services/firebase';

// Normalize Firebase user into an auth session object
async function toSession(user) {
  if (!user) return null;
  const tokenResult = await getIdTokenResult(user, /* forceRefresh */ false);
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    phoneNumber: user.phoneNumber,
    emailVerified: user.emailVerified,
    providerId: user.providerId,
    token: tokenResult.token,
    claims: tokenResult.claims || {},
    // expirationTime is an ISO string; convert to epoch ms
    expiresAt: new Date(tokenResult.expirationTime).getTime(),
    authTime: tokenResult.authTime ? new Date(tokenResult.authTime).getTime() : undefined,
    issuedAt: tokenResult.issuedAtTime ? new Date(tokenResult.issuedAtTime).getTime() : undefined,
  };
}

// Subscribe to auth state changes; returns an unsubscribe function
export function subscribeAuth(onSession, onError) {
  // Combine onAuthStateChanged and onIdTokenChanged so we capture both sign-in/out and token refresh/expiration events
  const unsubs = [];

  // Fires on sign-in/out
  const unsubAuth = onAuthStateChanged(auth, async (user) => {
    try {
      const session = await toSession(user);
      onSession?.(session);
    } catch (e) {
      onError?.(e);
    }
  }, (err) => onError?.(err));
  unsubs.push(unsubAuth);

  // Fires when the ID token changes (refresh/expiration/claims update)
  const unsubToken = onIdTokenChanged(auth, async (user) => {
    try {
      const session = await toSession(user);
      onSession?.(session);
    } catch (e) {
      onError?.(e);
    }
  }, (err) => onError?.(err));
  unsubs.push(unsubToken);

  return () => {
    unsubs.forEach((fn) => {
      try { fn(); } catch (_) {}
    });
  };
}

export async function signOutUser() {
  await signOut(auth);
}
